<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

        <!--Import Data-->
    <ImportModal
        :form-import-data="formImportData"
        :alert="defaultAlert"
        errors-title="Problems and notes"
        ask-source-system
        v-model:source-system="formImportData.source_system"
        source-system-placeholder="e.g. quickbooks (the same name you used for its companies)"
        @file-change="onFileImportChange"
        @import="importRecords"
        @download-template="downloadImportTemplate"
    >
        <!--Import Notes-->
        <div id="accordion">
            <div class="card">
                <div class="card-header">
                    <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                        Import Process
                    </a>
                </div>
                <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
                    <div class="card-body">
                        <ol>
                            <li>Import <b>companies first</b> (Companies page), then come back here.</li>
                            <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b>: it has the same columns.</li>
                            <li><b>Old system ID</b>: the branch's ID in your old system. Branches whose Old system ID was already imported are skipped.</li>
                            <li><b>Old system company ID</b>: the Old system ID of a company you imported earlier. Leave it blank to add the branch to the company you are logged into.</li>
                            <li><b>Branch code</b> must be new in its company (deleted branches count too).</li>
                            <li><b>Head office</b>: 1 for one branch per company only. Every imported company already has a Head Office, so such rows are imported as normal branches, with a note.</li>
                            <li>Enter the <b>same Old system name</b> you used when importing the companies (e.g. quickbooks).</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
        <!--End Import Notes-->
    </ImportModal>
    <!--Import Data-->

    <!--Filter Data Modal (shared: FilterModal.vue)-->
    <FilterModal
        title="Filter branches"
        :draft="filterDraft"
        v-model:search-by="tableData.searchBy"
        :view-options="viewOptions"
        :search-by-options="searchByOptions"
        :alert="defaultAlert"
        @search-by-change="changePlaceholder()"
        @apply="applyFilters"
        @clear="clearFilters"
    />
    <!--End Filter Data Modal-->


<!--Begin form-->
<RecordFormModal
    :title="isEditing ? 'Update Branch Details' : 'Add Branch Details'"
    title-icon="bi bi-building"
    :is-editing="isEditing"
    :is-saving="isSaving"
    :alert="defaultAlert"
    @save="saveOrEditRecord"
    @sample="addSampleData"
    @reset="resetForm"
>
    <!-- Hidden ID -->
    <HiddenField v-model="formInputs.id" />

    <!-- Branch Name -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.branch_name"
            col="col-md-12"
            id="branchNameInput"
            label="Branch Name"
            icon="fa fa-building"
            placeholder="Enter branch name"
            tooltip="Please enter the branch name"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Company -->
    <div class="row g-3 mb-0">
        <SelectField
            v-model="formInputs.owner_company_id"
            col="col-md-12"
            id="companyInput"
            name="owner_company_id"
            label="Company"
            icon="fa fa-building"
            :options="companies"
            :reduce="company => Number(company.id)"
            option-label="company_name"
            placeholder="Select company"
            :disabled="isEditing"
            :hint="isEditing ? 'A branch cannot be moved to another company.' : ''"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
        />
    </div>

    <!-- Branch Code -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.branch_code"
            col="col-md-12"
            id="branchCodeInput"
            label="Branch Code"
            icon="fa fa-hashtag"
            placeholder="Enter branch code"
            tooltip="Enter the branch code"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Email & Phone -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.email"
            col="col-md-6"
            id="emailInput"
            type="email"
            label="Email"
            icon="fa fa-envelope"
            placeholder="Enter email"
            tooltip="Enter a valid branch email"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.phone"
            col="col-md-6"
            id="phoneInput"
            type="tel"
            label="Phone"
            icon="fa fa-phone"
            placeholder="Enter phone number"
            tooltip="Enter the branch phone number"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Address & City -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.address"
            col="col-md-6"
            id="addressInput"
            label="Address"
            icon="fa fa-home"
            placeholder="Enter address"
            tooltip="Enter the branch address"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.city"
            col="col-md-6"
            id="cityInput"
            label="City"
            icon="fa fa-map-marker"
            placeholder="Enter city"
            tooltip="Enter the branch city"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Country & Postal Code -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.country"
            col="col-md-6"
            id="countryInput"
            label="Country"
            icon="fa fa-globe"
            placeholder="Enter country"
            tooltip="Enter the branch country"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.postal_code"
            col="col-md-6"
            id="postalCodeInput"
            label="Postal Code"
            icon="fa fa-envelope-open"
            placeholder="Enter postal code"
            tooltip="Enter the postal code"
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Status -->
    <div class="row g-3 mb-0">
        <CheckField
            v-model="formInputs.is_active"
            col="col-md-6"
            id="isActiveInput"
            label="Status"
            text="Active"
        />
    </div>
</RecordFormModal>
<!--End form-->


    <h4 class="sa-core-color fw-semibold mb-2">
        <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
    </h4>
    <!--Tables-->
    <div class="card glass mt-0 bg-sa-card">
        <div class="card-body bg-sa-card-body p-0">


            <!-- Table action row (shared: TableToolbar.vue) -->
            <TableToolbar
                can-add
                :filter-count="activeFilterCount"
                :columns="tableColumns"
                v-model:hidden-columns="hiddenColumns"
                :selected-count="tableData.selectedRecordIds.length"
                :deleted-view="tableData.recordFilter === 'deletedone'"
                :can-delete="$hasPermission('branches.delete')"
                @add="clearForm()"
                @filter="openFilters"
                @clear-selection="clearSelection"
                @bulk-action="runBulkAction"
            >
                <!-- More menu -->
                <template #more>
                            <li><h6 class="dropdown-header">Data</h6></li>
                            <li>
                                <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                                    <i class="fa fa-upload fa-fw me-1"></i> Import
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                                    <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                                    <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                                </button>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                                    <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                                </button>
                            </li>
                </template>
            </TableToolbar>
            <!-- Table Search Panel -->
            <SearchBox
                v-model:search-query="tableData.searchQuery"
                v-model:records-per-page="tableData.recordsPerPage"
                :placeholder="tableData.searchPlaceHolderText"
                @search-input="onSearchInput"
                @search-click="onSearchButtonClick"
                @per-page-change="onRecordsPerPageChange"
            />
            <!-- What the table is currently showing -->
            <ActiveFilters
                :chips="activeFilterChips"
                @remove="removeFilterChip"
                @clear="clearAllFilterChips"
            />
            <!-- Table -->
            <div class="table-responsive">
                

<table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
        <thead>
        <tr>
            <th>
                <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
            </th>
            <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
            <th class="button hide-sm-td">No</th>
            <SortableTh v-if="isColumnVisible('branch_name')" column="branch_name" :table-data="tableData" @sort="orderBy">Branch Name</SortableTh>
            <SortableTh v-if="isColumnVisible('company')" column="owner_company_id" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Company</SortableTh>
            <SortableTh v-if="isColumnVisible('branch_code')" column="branch_code" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Code</SortableTh>
            <SortableTh v-if="isColumnVisible('phone')" column="phone" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Phone</SortableTh>
            <SortableTh v-if="isColumnVisible('city')" column="city" :table-data="tableData" @sort="orderBy" class="hide-sm-td">City</SortableTh>
            <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
            <th class="button">Action</th>
        </tr>
    </thead>

    <tbody>
        <template v-for="(record, index) in tableData.records" :key="record.id">

            <!-- Main Row -->
            <tr class="clickable-row"
                :data-record-id="record.id"
                :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                @click="$onRowClick(record, $event)"
                @dblclick="$onRowDblClick(record, $event)"
            >
                <td>
                    <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
                </td>
                <td class="expand-cell hide-sm-td"
                    :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                    @click="$toggleDetails(record.id)">
                <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                </td>
                <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                <td v-if="isColumnVisible('branch_name')">
                    <!-- Phones: + / − and the number go in front of the branch (their columns are hidden) -->
                    <span
                        class="show-sm-inline expand-cell me-2"
                        :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                        @click="$toggleDetails(record.id)"
                    >
                        <i class="fa fa-lg expand-icon"
                           :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                           :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
                    </span>
                    <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.branch_name }}
                    <span v-if="Number(record.is_head_office) === 1" class="badge bg-primary ms-1">Head Office</span>
                </td>
                <td v-if="isColumnVisible('company')" class="hide-sm-td">{{ record.company_name }}</td>
                <td v-if="isColumnVisible('branch_code')" class="hide-sm-td">{{ record.branch_code }}</td>
                <td v-if="isColumnVisible('phone')" class="hide-sm-td">{{ record.phone }}</td>
                <td v-if="isColumnVisible('city')" class="hide-sm-td">{{ record.city }}</td>
                <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
                    <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-danger'">
                        {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                    </span>
                </td>
                <td>
                    <RowActions
                        :record="record"
                        :modal-id="'deleteCrudModal' + record.id"
                        :can-soft-delete="$hasPermission('branches.delete')
                            && Number(record.is_head_office) !== 1
                            && Number(record.id) !== Number($store.state.branch_id)"
                        :can-restore="$hasPermission('branches.delete')"
                        @edit="editRecord"
                        @delete-type="setDeleteType"
                    />

                    <DeleteModal
                        :modal-id="'deleteCrudModal' + record.id"
                        :title="deleteModalTitle"
                        :message="deleteModalMessage"
                        @confirm="deleteRecord(record.id)"
                    />
                </td>
            </tr>

            <!-- Hidden Expanded Row -->
            <tr class="collapse-row">
                <td class="p-0"></td>
                <td class="p-0 hide-sm-td"></td>
                <td colspan="8" class="p-0">
                    <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                        <div class="p-3 details-list">
                            <div><i class="fa fa-hashtag"></i><strong>Branch Number:</strong> {{ record.branch_number }}</div>
                            <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                            <div><i class="fa fa-building-o"></i><strong>Creator Company:</strong> {{ record.creator_company_name }}</div>

                            <!-- Shown on phones, or always when the column is hidden with the Columns button -->
                            <div :class="{ 'show-sm-td': isColumnVisible('branch_name') }"><i class="fa fa-sitemap"></i><strong>Branch Name:</strong> {{ record.branch_name }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('company') }"><i class="fa fa-building"></i><strong>Company:</strong> {{ record.company_name }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('branch_code') }"><i class="fa fa-barcode"></i><strong>Branch Code:</strong> {{ record.branch_code }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('phone') }"><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('city') }"><i class="fa fa-map-marker"></i><strong>City:</strong> {{ record.city }}</div>
                            <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                                <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                                <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-danger'">
                                    {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                                </span>
                            </div>

                            <!-- Always in details -->
                            <div><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.email }}</div>
                            <div><i class="fa fa-home"></i><strong>Address:</strong> {{ record.address }}</div>
                            <div><i class="fa fa-globe"></i><strong>Country:</strong> {{ record.country }}</div>
                            <div><i class="fa fa-envelope-open"></i><strong>Postal Code:</strong> {{ record.postal_code }}</div>
                            <div><i class="fa fa-flag"></i><strong>Head Office:</strong> {{ Number(record.is_head_office) === 1 ? 'Yes' : 'No' }}</div>
                            <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>

                            <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                            <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                            <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                            <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                        </div>
                    </div>
                </td>
            </tr>

        </template>

        <!-- No rows: blank while loading, then the empty message in the middle -->
        <tr v-if="tableData.records.length === 0" class="table-filler">
            <td colspan="10" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                <TableEmptyState
                    v-if="!tableLoading"
                    :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                    :can-add="true"
                    :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No branches yet'"
                    :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Click Add to create the first branch.'"
                    icon="fa fa-sitemap"
                    @add="clearForm()"
                    @clear="clearAllFilterChips"
                />
            </td>
        </tr>
    </tbody>

    <tfoot class="table-light">
        <tr>
            <td colspan="10">
                <!-- Pagination (hidden when there are no rows; its space is kept) -->
                <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                <Pagination
                    v-else
                    :currentPage="tableData.currentPage"
                    :totalPages="tableData.totalPages"
                    :hasNextPage="tableData.hasNextPage"
                    :totalRecords="tableData.totalRecords"
                    :startRecord="tableData.startRecord"
                    :endRecord="tableData.endRecord"
                    :showFullPagination="tableData.totalRecords > 0"
                    :lastKnownPage="lastKnownPage"
                    @page-changed="fetchRecords"
                    @loaderMessage="updateLoaderMessage"
                    @last-page-request="goToLastPage"
                />
            </td>
        </tr>
    </tfoot>
</table>
            </div>
            <!-- Bottom Controls: Delete & Pagination -->

        </div>
    </div>
    <!--End Tables-->
</template>
<script>
    import PageStatus from "../../../components/admin/PageStatus.vue";
    import TableToolbar from "../../../components/admin/TableToolbar.vue";
    import FilterModal from "../../../components/admin/FilterModal.vue";
    import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import SortableTh from "../../../components/admin/SortableTh.vue";
    import DeleteModal from "../../../components/admin/DeleteModal.vue";
    import RowActions from "../../../components/admin/RowActions.vue";
    import SearchBox from "../../../components/admin/SearchBox.vue";
    import ImportModal from "../../../components/admin/ImportModal.vue";
    import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import HiddenField from "../../../components/admin/HiddenField.vue";
    import CheckField from "../../../components/admin/CheckField.vue";

    import BranchesMixin from "./mixins/BranchesMixin.js";
    import tableMixin from "@/mixins/tableMixin.js";
    import axios from "axios";

    export default {
        name: "Branches",
        mixins: [tableMixin, BranchesMixin], // shared table methods first, then Branches' own
        components: {
            PageStatus,
            TableToolbar,
            FilterModal,
            ActiveFilters,
            Pagination,
            TableEmptyState,
            SortableTh,
            DeleteModal,
            RowActions,
            SearchBox,
            ImportModal,
            RecordFormModal,
            FormField,
            SelectField,
            HiddenField,
            CheckField,
        },
        data() {
            return {
                // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
                standardPageSetup: true,                  // tableMixin sets up Enter-to-save and table shortcuts
                apiUrl: "/settings/api_branches.php",     // used by tableMixin's fetchRecords, delete, import, export
                importTemplateFileName: "branches_import_template.xlsx",
                exportFileName: "branchesExport",         // name of the exported file
                columnsKey: "branches",                   // saves this page's hidden columns (Columns button)
                pageIcon: 'fa fa-sitemap',                // same icon as this page in the sidebar
                companies: [],
                formInputs: this.getDefaultFormInputs(),
                // Filter window: View
                viewOptions: [
                    { label: "All Branches", value: "all" },
                    { label: "Branches By Me", value: "byme" },
                    { label: "Deleted Branches", value: "deletedone" },
                ],
                // Filter window: Search By
                searchByOptions: [
                    { label: "Branch Name", value: "branch_name" },
                    { label: "Branch Code", value: "branch_code" },
                    { label: "Branch Number", value: "branch_number" },
                    { label: "Email", value: "email" },
                    { label: "Phone", value: "phone" },
                    { label: "City", value: "city" },
                    { label: "Country", value: "country" },
                ],
            };
        },
        mounted() {
            this.openAddFromRoute();
        },
        async created() {
            //alert(this.$store.state.isLoggedIn);
            //this.retriveUserDetails();
            this.loadCompanies();
            await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
            this.$checkIfCanFetchLocation(); //Only applies if location is enabled for user. If not Log user out for him to enable location
        },
        computed: {
            // Page heading that follows the chosen filter.
            pageTitle() {
                const titles = {
                    all: "Branches",
                    byme: "Branches By Me",
                    deletedone: "Branches Deleted Records",
                };
                return titles[this.tableData.recordFilter] || "Branches";
            },
            // Columns the user can show or hide (Columns button)
            tableColumns() {
                return [
                    { key: "branch_name", label: "Branch Name" },
                    { key: "company", label: "Company" },
                    { key: "branch_code", label: "Code" },
                    { key: "phone", label: "Phone" },
                    { key: "city", label: "City" },
                    { key: "is_active", label: "Status" },
                ];
            },
        },
        watch: {
            // TopBar "+ Add branch" while already on this page
            "$route.query.add"(val) {
                if (val) this.openAddFromRoute();
            },
        },
        methods: {
            // Opens the Add form when the URL has ?add=1, with the company already chosen (?company=ID)
            async openAddFromRoute() {
                if (!this.$route.query.add) return;
                const companyId = Number(this.$route.query.company) || "";
                this.$router.replace({ query: {} });

                // The dropdown needs its list first, or it shows the ID instead of the name
                if (!this.companies.length) {
                    await this.loadCompanies();
                }

                this.$nextTick(() => {
                    document.getElementById('addRecordBtn')?.click();
                    if (companyId) this.formInputs.owner_company_id = companyId;
                });
            },
            // Selection bar: a Head Office, or the branch you are logged into, is never deleted
            // (same rule as the row menu). Take them out of the ticked rows, then run the shared bulk action.
            runBulkAction(deleteType = "soft") {
                if (deleteType === "soft") {
                    const protectedIds = this.tableData.records
                        .filter((record) => Number(record.is_head_office) === 1
                            || Number(record.id) === Number(this.$store.state.branch_id))
                        .map((record) => String(record.id));
                    const kept = this.tableData.selectedRecordIds.filter((id) => !protectedIds.includes(String(id)));
                    if (!kept.length) {
                        this.handleAlert("ending", "A Head Office or the branch you are logged into can't be deleted.", "warning", "page");
                        return;
                    }
                    this.tableData.selectedRecordIds = kept;
                }
                return tableMixin.methods.runBulkAction.call(this, deleteType);
            },
            getDefaultFormInputs() {
                return {
                    // Form Data
                    id: "",

                    owner_company_id: "",

                    branch_name: "",
                    branch_code: "",
                    email: "",
                    phone: "",

                    address: "",
                    city: "",
                    country: "",
                    postal_code: "",

                    is_active: true,
                };
            },
            getDefaultFormImportData() {
                return {
                    dataFile: null,
                    source_system: "",      // old system name, e.g. quickbooks
                    templateLoading: false, // spinner on the Download Import Template button
                    importErrors: [],       // rows not imported, or imported with notes
                    uploading: false,
                    progress: 0,
                    message: "",
                    messageType: "",
                };
            },
            // Table defaults: the standard ones (tableMixin) + this page's own
            getDefaultTableData() {
                return {
                    ...this.baseTableData(),
                    searchBy: "branch_name",
                    searchPlaceHolderText: "Search by Branch Name",
                };
            },
            addSampleData() {
                this.formInputs = {
                    ...this.getDefaultFormInputs(),
                    id: "",
                    // First company from the dropdown list (same Number() format the v-select uses)
                    owner_company_id: this.companies.length ? Number(this.companies[0].id) : "",
                    branch_name: "Nairobi Branch",
                    branch_code: "NBO-001",
                    email: "nairobi@example.com",
                    phone: "0711930184",
                    address: "P.O. Box 12345",
                    city: "Nairobi",
                    country: "Kenya",
                    postal_code: "00100",
                    is_active: true,
                };

                this.submitted = false;
                this.$clearValidationStates("#recordForm");
                this.$scrollToTopModal("recordFormModal");
            },
            saveOrEditRecord() {
                if (this.isSaving) return;
                this.isSaving = true;

                try {
                this.$scrollToTopModal("recordFormModal");
                this.submitted = true;

                const formIsValid = this.$validateForm("#recordForm");
                if (!formIsValid) {
                    this.$playErrorSound();
                        this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                    this.isSaving = false;
                    return;
                }
                //if (!this.$handleFormValidation('#recordForm')) { return; }
                this.handleAlert("starting", "Please wait submiting", "info", "modal");
                let formData = this.formData_fields();

                if (!this.formInputs.id) {
                    this.tableData.currentPage = 1;
                }

                axios
                    .post(this.apiUrl, formData, this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleSaveOrEditSuccess(response);
                        } else {
                            this.handleError("starting", response.data.message[0], "danger", "modal");
                        }
                    })
                    .catch((error) => {
                        this.handleRequestError(error, "modal");
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });} catch (error) {
                    this.isSaving = false;
                    throw error;
                }
            },
            formData_fields() {
                let formData = new FormData();
                formData.append("id", this.formInputs.id);
                formData.append("owner_company_id", this.formInputs.owner_company_id);
                formData.append("branch_name", this.formInputs.branch_name);
                formData.append("branch_code", this.formInputs.branch_code);
                formData.append("email", this.formInputs.email);
                formData.append("phone", this.formInputs.phone);
                formData.append("address", this.formInputs.address);
                formData.append("city", this.formInputs.city);
                formData.append("country", this.formInputs.country);
                formData.append("postal_code", this.formInputs.postal_code);
                formData.append("is_active", this.formInputs.is_active);

                formData.append("latitude", this.$store.state.latitude);
                formData.append("longitude", this.$store.state.longitude);

                formData.append("action", "saveOrEditRecord");

                return formData;
            },
            editRecord(records) {
                this.isEditing = true;
                //alert(records.owner_company_id);
                this.formInputs.id = records.id ?? "";
                this.formInputs.owner_company_id = Number(records.owner_company_id);
                this.formInputs.branch_name = records.branch_name ?? "";
                this.formInputs.branch_code = records.branch_code ?? "";
                this.formInputs.email = records.email ?? "";
                this.formInputs.phone = records.phone ?? "";
                this.formInputs.address = records.address ?? "";
                this.formInputs.city = records.city ?? "";
                this.formInputs.country = records.country ?? "";
                this.formInputs.postal_code = records.postal_code ?? "";
                this.formInputs.is_active = Number(records.is_active) === 1;

                this.$clearValidationStates("#recordForm");
            },
            
            loadCompanies() {
                return axios
                    .get(
                        `${this.apiUrl}?action=fetchCompaniesToAddBranch&page=1&limit=10000&searchBy=company_name&search=&orderBy=company_name&orderDirection=ASC&filter=all`,
                        this.$handleConfig()
                    )
                    .then((response) => {
                        if (response.data.status == "success") {

                            this.companies = response.data.results || [];

                        } else {

                            if (
                                response.data?.code === "JWT_EXPIRED" ||
                                response.data?.code === "CSRF_INVALID" ||
                                response.data?.code === "AUTH_REQUIRED"
                            ) {
                                this.$forceLogOut("danger", response.data.message);
                                return;
                            }

                            this.companies = [];

                            this.handleAlert(
                                "starting",
                                response.data.message,
                                "danger",
                                "modal"
                            );
                        }
                    })
                    .catch((error) => {

                        this.companies = [];

                        if (error.message === "Network Error") {

                            this.handleAlert(
                                "ending",
                                "No internet connection. Please check your network.",
                                "danger",
                                "modal"
                            );

                        } else {

                            this.handleAlert(
                                "ending",
                                "Something went wrong while loading companies.",
                                "danger",
                                "modal"
                            );
                        }
                    });
            },
            
    
        },
    };
</script>